import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';
import StudentHeader from '../components/StudentHeader';
import { studentFetch } from '../utils/authFetch';
import * as THREE from 'three';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const SUBJECT_EMOJI = {
  'Mathematics': '🔢', 'Physics': '⚡', 'Chemistry': '🧪',
  'Biology': '🧬', 'English Language': '🔤', 'English Literature': '📖',
  'Arabic': '✍️', 'Computer Science': '💻', 'Business Studies': '💼',
  'Economics': '📈', 'History': '🏛️', 'Geography': '🌍',
  'Islamiyat': '☪️', 'Islamic Studies': '☪️', 'Science': '🔬',
  'Sociology': '👥', 'Psychology': '🧠', 'Accounting': '📊',
  'Information Technology': '🖥️', 'Art and Design': '🎨',
  'Physical Education': '🏃', 'Environmental Science': '🌿', 'Statistics': '📉',
};

const SUBJECT_META = {
  'Mathematics':              { icon:'🔢', ac:'#7c3aed', ibg:'#ede9fe', sh:'rgba(124,58,237,.22)',  tilt:'rotate(-1.5deg)' },
  'Science':                  { icon:'🔬', ac:'#0891b2', ibg:'#cffafe', sh:'rgba(8,145,178,.2)',    tilt:'rotate(1deg)'    },
  'English':                  { icon:'📖', ac:'#d97706', ibg:'#fef3c7', sh:'rgba(217,119,6,.2)',    tilt:'rotate(-0.8deg)' },
  'Computing':                { icon:'💻', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(1.2deg)'  },
  'Computer Science':         { icon:'💻', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(1.2deg)'  },
  'ICT':                      { icon:'💻', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(1.2deg)'  },
  'Global Perspectives':      { icon:'🌍', ac:'#0ea5e9', ibg:'#e0f2fe', sh:'rgba(14,165,233,.2)',   tilt:'rotate(-1deg)'   },
  'Modern Foreign Language':  { icon:'🗣️', ac:'#ec4899', ibg:'#fce7f3', sh:'rgba(236,72,153,.2)',   tilt:'rotate(0.8deg)'  },
  'التربية الإسلامية':        { icon:'☪️',  ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)',    tilt:'rotate(-1.2deg)' },
  'اللغة العربية':            { icon:'✍️',  ac:'#b45309', ibg:'#fef3c7', sh:'rgba(180,83,9,.2)',     tilt:'rotate(1.5deg)'  },
  'تاريخ قطر والمواطنة':      { icon:'🇶🇦', ac:'#dc2626', ibg:'#fee2e2', sh:'rgba(220,38,38,.2)',    tilt:'rotate(-1deg)'   },
  'Physics':                  { icon:'⚛️',  ac:'#6d28d9', ibg:'#ede9fe', sh:'rgba(109,40,217,.2)',   tilt:'rotate(1deg)'    },
  'Chemistry':                { icon:'🧪', ac:'#16a34a', ibg:'#dcfce7', sh:'rgba(22,163,74,.2)',    tilt:'rotate(-1deg)'   },
  'Biology':                  { icon:'🧬', ac:'#0891b2', ibg:'#cffafe', sh:'rgba(8,145,178,.2)',    tilt:'rotate(1.2deg)'  },
  'History':                  { icon:'📜', ac:'#b45309', ibg:'#fef3c7', sh:'rgba(180,83,9,.2)',     tilt:'rotate(-0.8deg)' },
  'Geography':                { icon:'🌍', ac:'#0ea5e9', ibg:'#e0f2fe', sh:'rgba(14,165,233,.2)',   tilt:'rotate(1deg)'    },
};
const DEFAULT_META = { icon:'📚', ac:'#059669', ibg:'#d1fae5', sh:'rgba(5,150,105,.2)', tilt:'rotate(0deg)' };

const T = {
  ar: {
    pageTitle:       'بطاقات المراجعة',
    pageSubtitle:    'اختر مادة للمراجعة',
    chooseSubject:   'اختر مادة',
    chooseUnit:      'اختر الوحدة',
    back:            '→ رجوع',
    breadcrumbSubjects: 'المواد',
    noCards:         'ابدأ الشرح أولاً',
    cardCount:       (n) => `${n} بطاقة`,
    unitCount:       (n) => `${n} وحدات`,
    flipPrompt:      '↺ اضغط للإجابة',
    flipBack:        '↺ ارجع للسؤال',
    confidenceLabel: 'ما مدى ثقتك؟',
    progress:        (cur, total) => `${cur} / ${total}`,
    toastExcellent:  'ممتاز! +10 XP 🤩',
    toastGood:       'كويس! +5 XP 👍',
    toastRetry:      'راجع تاني! 💪',
    completionTitle: 'أحسنت! راجعت كل البطاقات',
    completionBtn:   'مراجعة مرة أخرى',
    loading:         'جاري التحميل...',
    error:           'حدث خطأ، حاول مرة أخرى',
    retry:           'إعادة المحاولة',
    xpBadge:         '+10 XP',
  },
  en: {
    pageTitle:       'Flashcards',
    pageSubtitle:    'Choose a subject to review',
    chooseSubject:   'Choose Subject',
    chooseUnit:      'Choose Unit',
    back:            '← Back',
    breadcrumbSubjects: 'Subjects',
    noCards:         'Start learning first',
    cardCount:       (n) => `${n} cards`,
    unitCount:       (n) => `${n} units`,
    flipPrompt:      '↺ Tap to reveal',
    flipBack:        '↺ Back to question',
    confidenceLabel: 'How confident are you?',
    progress:        (cur, total) => `${cur} / ${total}`,
    toastExcellent:  'Excellent! +10 XP 🤩',
    toastGood:       'Good! +5 XP 👍',
    toastRetry:      'Review again! 💪',
    completionTitle: 'Well done! You reviewed all cards',
    completionBtn:   'Review Again',
    loading:         'Loading...',
    error:           'Something went wrong, try again',
    retry:           'Retry',
    xpBadge:         '+10 XP',
  },
};

export default function Flashcards() {
  const { lang }   = useLanguage();
  const t          = T[lang] || T.ar;
  const dir        = lang === 'ar' ? 'rtl' : 'ltr';

  const studentRaw = localStorage.getItem('student_data');
  const student    = studentRaw ? JSON.parse(studentRaw) : null;
  const token      = localStorage.getItem('student_token');

  const [screen,          setScreen]          = useState('subjects');
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);

  // Screen 1
  const [subjects,        setSubjects]        = useState([]);

  // Screen 2
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [units,           setUnits]           = useState([]);
  const [openSemesters,   setOpenSemesters]   = useState({ 1: true, 2: false });

  // Screen 3
  const [selectedUnit,    setSelectedUnit]    = useState(null);
  const [cards,           setCards]           = useState([]);
  const [currentIndex,    setCurrentIndex]    = useState(0);
  const [isFlipped,       setIsFlipped]       = useState(false);
  const [confidence,      setConfidence]      = useState(null);
  const [flipAnimating,   setFlipAnimating]   = useState(false);
  const [toast,           setToast]           = useState(null);
  const [completed,       setCompleted]       = useState(false);

  const cardFaceRef  = useRef(null);
  const defaultTilt  = 'rotateX(4deg) rotateY(-6deg)';

  // ── Load subjects on mount ─────────────────────────────────────────────────
  const loadSubjects = useCallback(async () => {
    if (!student || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await studentFetch(`${API}/student/subjects/${student.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res) return;
      const data = await res.json();
      if (data.success) {
        setSubjects(data.subjects || []);
      } else {
        setError(t.error);
      }
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  }, [student?.id, token]); // eslint-disable-line

  useEffect(() => { loadSubjects(); }, [loadSubjects]);

  // ── Apply default tilt whenever the card screen/index changes ─────────────
  useEffect(() => {
    if (screen === 'cards' && cardFaceRef.current && !isFlipped) {
      cardFaceRef.current.style.transition = '';
      cardFaceRef.current.style.transform  = defaultTilt;
    }
  }, [screen, currentIndex]); // eslint-disable-line

  // ── Screen 1 → 2 ──────────────────────────────────────────────────────────
  async function handleSubjectClick(subj) {
    setLoading(true);
    setError(null);
    setSelectedSubject({
      id:    subj.subject_id,
      name:  subj.subject_name,
      emoji: subj.subject_emoji || SUBJECT_EMOJI[subj.subject_name] || '📚',
    });
    try {
      const res  = await studentFetch(`${API}/student/flashcard-units/${subj.subject_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res) return;
      if (!res.ok) {
        console.error('flashcard-units API error:', res.status);
        setError(t.error);
        return;
      }
      const data = await res.json();
      setUnits(Array.isArray(data) ? data : []);
      setScreen('units');
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  }

  // ── Screen 2 → 3 ──────────────────────────────────────────────────────────
  async function handleUnitClick(unit) {
    if (unit.card_count === 0) return;
    setLoading(true);
    setError(null);
    setSelectedUnit(unit);
    try {
      const res  = await studentFetch(`${API}/student/flashcards/${unit.unit_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res) return;
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        console.error('Flashcards API error:', res.status, err);
        setError(t.error);
        return;
      }
      const data = await res.json();
      setCards(Array.isArray(data) ? data : []);
      setCurrentIndex(0);
      setIsFlipped(false);
      setConfidence(null);
      setCompleted(false);
      setScreen('cards');
    } catch {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  }

  // ── Flip (scaleX squeeze, not CSS 3D) ─────────────────────────────────────
  function handleCardClick() {
    if (flipAnimating) return;
    const face = cardFaceRef.current;
    if (!face) return;
    setFlipAnimating(true);

    face.style.transition = 'transform 280ms cubic-bezier(.4,0,.8,0)';
    face.style.transform  = 'scaleX(0)';

    setTimeout(() => {
      setIsFlipped(f => !f);
      face.style.transition = 'transform 280ms cubic-bezier(.2,1,.6,1)';
      face.style.transform  = 'scaleX(1)';
      setTimeout(() => setFlipAnimating(false), 280);
    }, 280);
  }

  // ── Parallax ──────────────────────────────────────────────────────────────
  function handleMouseMove(e) {
    if (isFlipped || !cardFaceRef.current) return;
    const face = cardFaceRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const dx   = (e.clientX - (rect.left + rect.width  / 2)) / (rect.width  / 2);
    const dy   = (e.clientY - (rect.top  + rect.height / 2)) / (rect.height / 2);
    face.style.transition = 'transform 0.08s';
    face.style.transform  = `rotateX(${-dy * 8}deg) rotateY(${dx * 8}deg)`;
  }

  function handleMouseLeave() {
    const face = cardFaceRef.current;
    if (!face) return;
    face.style.transition = 'transform 300ms ease';
    face.style.transform  = isFlipped ? 'rotateX(0deg) rotateY(0deg)' : defaultTilt;
  }

  // ── Confidence ────────────────────────────────────────────────────────────
  function handleConfidence(score) {
    setConfidence(score);
    const msg = score === 5 ? t.toastExcellent : score >= 3 ? t.toastGood : t.toastRetry;
    setToast(msg);
    setTimeout(() => setToast(null), 800);
    setTimeout(() => {
      if (currentIndex + 1 >= cards.length) {
        setCompleted(true);
      } else {
        setCurrentIndex(i => i + 1);
        setIsFlipped(false);
        setConfidence(null);
      }
    }, 800);
  }

  function resetCards() {
    setCurrentIndex(0);
    setIsFlipped(false);
    setConfidence(null);
    setCompleted(false);
  }

  const card = cards[currentIndex] || null;

  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const w = canvas.parentElement?.clientWidth  || window.innerWidth;
    const h = canvas.parentElement?.clientHeight || window.innerHeight;
    renderer.setSize(w, h);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, w / h, 0.1, 100);
    camera.position.z = 7;
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const L1 = new THREE.DirectionalLight(0xffffff, 1.2); L1.position.set(6, 8, 4); scene.add(L1);
    const L2 = new THREE.DirectionalLight(0xffffff, 0.6); L2.position.set(-5, -3, 4); scene.add(L2);
    const meshes = [];
    [
      [new THREE.IcosahedronGeometry(0.36, 0), -4, 2.5, -1.2],
      [new THREE.IcosahedronGeometry(0.24, 0), 3.6, 2, -2],
      [new THREE.OctahedronGeometry(0.30, 0), -3, -1.8, -1.5],
      [new THREE.OctahedronGeometry(0.20, 0), 2.8, -2.2, -1],
      [new THREE.TetrahedronGeometry(0.26, 0), 0.7, 2.8, -1.8],
      [new THREE.TetrahedronGeometry(0.16, 0), -1.4, -2.6, -1.4],
      [new THREE.IcosahedronGeometry(0.14, 0), 4, 0.5, -0.6],
      [new THREE.OctahedronGeometry(0.12, 0), -4, -0.7, -0.6],
      [new THREE.IcosahedronGeometry(0.11, 0), 1.8, -3, -0.9],
      [new THREE.TetrahedronGeometry(0.13, 0), -2, 3, -0.9],
    ].forEach(([G, x, y, z]) => {
      const mat = new THREE.MeshPhongMaterial({ color: 0x34D399, transparent: true, opacity: 0.22, shininess: 120, specular: new THREE.Color(0xffffff) });
      const m = new THREE.Mesh(G, mat);
      m.position.set(x, y, z);
      m.rotation.set(Math.random() * Math.PI * 2, Math.random() * Math.PI * 2, Math.random() * Math.PI * 2);
      m._oy = y; m._fs = 0.2 + Math.random() * 0.5; m._fa = 0.12 + Math.random() * 0.22;
      m._rx = (0.002 + Math.random() * 0.006) * (Math.random() > 0.5 ? 1 : -1);
      m._ry = (0.002 + Math.random() * 0.005) * (Math.random() > 0.5 ? 1 : -1);
      m._ph = Math.random() * Math.PI * 2;
      scene.add(m); meshes.push(m);
    });
    const PC = 120;
    const pp = new Float32Array(PC * 3);
    const pv = [];
    for (let i = 0; i < PC; i++) {
      pp[i * 3] = (Math.random() - 0.5) * 16;
      pp[i * 3 + 1] = (Math.random() - 0.5) * 12;
      pp[i * 3 + 2] = (Math.random() - 0.5) * 5 - 1.5;
      pv.push({ vx: (Math.random() - 0.5) * 0.007, vy: (Math.random() - 0.5) * 0.007 });
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(pp, 3));
    const pMat = new THREE.PointsMaterial({ color: 0x34D399, size: 0.07, transparent: true, opacity: 0.5, sizeAttenuation: true });
    scene.add(new THREE.Points(pGeo, pMat));
    const lp = new Float32Array(PC * PC * 6);
    const lGeo = new THREE.BufferGeometry();
    lGeo.setAttribute('position', new THREE.BufferAttribute(lp, 3));
    const lMesh = new THREE.LineSegments(lGeo, new THREE.LineBasicMaterial({ color: 0x34D399, transparent: true, opacity: 0.1 }));
    scene.add(lMesh);
    let mx = 0, my = 0;
    const onMM = (e) => { mx = (e.clientX / window.innerWidth - 0.5) * 2; my = (e.clientY / window.innerHeight - 0.5) * 2; };
    window.addEventListener('mousemove', onMM);
    const onResize = () => {
      const w2 = canvas.parentElement?.clientWidth  || window.innerWidth;
      const h2 = canvas.parentElement?.clientHeight || window.innerHeight;
      camera.aspect = w2 / h2;
      camera.updateProjectionMatrix();
      renderer.setSize(w2, h2);
    };
    window.addEventListener('resize', onResize);
    let t = 0, li = 0, animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      t += 0.007;
      meshes.forEach(m => {
        m.rotation.x += m._rx; m.rotation.y += m._ry;
        m.position.y = m._oy + Math.sin(t * m._fs + m._ph) * m._fa;
      });
      for (let i = 0; i < PC; i++) {
        pp[i * 3] += pv[i].vx; pp[i * 3 + 1] += pv[i].vy;
        if (Math.abs(pp[i * 3]) > 8) pv[i].vx *= -1;
        if (Math.abs(pp[i * 3 + 1]) > 6) pv[i].vy *= -1;
      }
      pGeo.attributes.position.needsUpdate = true;
      li = 0;
      for (let i = 0; i < PC; i++) for (let j = i + 1; j < PC; j++) {
        const dx = pp[i * 3] - pp[j * 3], dy = pp[i * 3 + 1] - pp[j * 3 + 1];
        if (dx * dx + dy * dy < 5) {
          lp[li++] = pp[i * 3]; lp[li++] = pp[i * 3 + 1]; lp[li++] = pp[i * 3 + 2];
          lp[li++] = pp[j * 3]; lp[li++] = pp[j * 3 + 1]; lp[li++] = pp[j * 3 + 2];
        }
      }
      lGeo.attributes.position.needsUpdate = true;
      lGeo.setDrawRange(0, li / 3);
      camera.position.x += (mx * 0.3 - camera.position.x) * 0.032;
      camera.position.y += (-my * 0.18 - camera.position.y) * 0.032;
      camera.lookAt(0, 0, 0);
      renderer.render(scene, camera);
    };
    animate();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('mousemove', onMM);
      window.removeEventListener('resize', onResize);
      renderer.dispose();
    };
  }, []);

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div style={S.root} dir={dir}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none' }} />
      <style>{CSS}</style>

      <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <StudentHeader
          title={lang === 'ar' ? 'بطاقات المراجعة' : 'Flashcards'}
          showBack
          onBack={() => window.history.back()}
          lang={lang}
        />

        {/* ═══ SCROLLABLE CONTENT AREA ═══ */}
        <div style={S.scrollArea}>

        {/* ═══ SCREEN 1: Subject Selection ═══ */}
        {screen === 'subjects' && (
          <div style={S.subjectsContent}>
            {loading && <Spinner />}
            {!loading && error && <ErrorMsg msg={error} retryLabel={t.retry} onRetry={loadSubjects} />}
            {!loading && !error && (
              <div className="fc-subject-grid" style={S.subjectGrid}>
                {subjects.map(subj => {
                  const meta = SUBJECT_META[subj.subject_name] || DEFAULT_META;
                  return (
                    <div
                      key={subj.subject_id}
                      className="fc-subject-card"
                      style={{ position: 'relative', cursor: 'pointer' }}
                      onClick={() => handleSubjectClick(subj)}
                    >
                      <div
                        style={{
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          gap: 10, padding: '24px 16px 20px', borderRadius: 24,
                          border: '2px solid #E5E7EB', background: 'white',
                          transform: meta.tilt,
                          boxShadow: '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07)',
                          transition: 'transform .15s, box-shadow .15s, border-color .15s',
                          position: 'relative', zIndex: 1,
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.transform = 'translateY(-6px)';
                          e.currentTarget.style.boxShadow = '0 5px 0 #D1D5DB, 0 18px 36px rgba(0,0,0,.1)';
                          e.currentTarget.style.borderColor = '#D1D5DB';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 5px 0 #E5E7EB, 0 10px 28px rgba(0,0,0,.07)';
                          e.currentTarget.style.borderColor = '#E5E7EB';
                        }}
                      >
                        <div style={{
                          width: 60, height: 60, borderRadius: 18,
                          background: meta.ibg, fontSize: 26,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transform: 'translateY(-6px)',
                          filter: 'drop-shadow(0 5px 9px rgba(0,0,0,.18))',
                          transition: 'transform .15s, filter .15s',
                        }}>
                          {meta.icon}
                        </div>
                        <span style={{
                          fontFamily: "'Sora', sans-serif", fontSize: 13,
                          fontWeight: 700, color: '#1e3a5f',
                          textAlign: 'center', lineHeight: 1.3,
                        }}>
                          {subj.subject_name}
                        </span>
                        <div style={{
                          width: 28, height: 3, borderRadius: 2,
                          background: meta.ac, opacity: 0.3, marginTop: 2,
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ═══ SCREEN 2: Unit Selection ═══ */}
        {screen === 'units' && (
          <>
            <div style={S.pageHeader}>
              <button style={S.backBtn} onClick={() => setScreen('subjects')}>{t.back}</button>
              <h2 style={S.screenTitle}>{selectedSubject?.name}</h2>
              <p style={S.breadcrumb}>{t.breadcrumbSubjects} › {selectedSubject?.name}</p>
            </div>
            <div style={S.content}>
              {loading && <Spinner />}
              {!loading && error && <ErrorMsg msg={error} retryLabel={t.retry} />}
              {!loading && !error && (() => {
                const bySemester = {};
                units.forEach(u => {
                  const s = u.semester || 1;
                  if (!bySemester[s]) bySemester[s] = [];
                  bySemester[s].push(u);
                });
                const semesters = Object.keys(bySemester).sort();

                function extractBab(name) {
                  if (name.startsWith('الباب الأول'))  return 'الباب الأول';
                  if (name.startsWith('الباب الثاني')) return 'الباب الثاني';
                  if (name.startsWith('الباب الثالث')) return 'الباب الثالث';
                  return null;
                }

                const renderAccUnit = (unit, displayName) => {
                  const hasCards = unit.card_count > 0;
                  const label = displayName !== undefined ? displayName : unit.unit_name;
                  const meta = SUBJECT_META[selectedSubject?.name] || DEFAULT_META;
                  return (
                    <div
                      key={unit.unit_id}
                      className={hasCards ? 'fc-acc-unit-card' : ''}
                      style={{
                        ...S.accUnitRow,
                        background: hasCards ? 'white' : '#F9FAFB',
                        border: hasCards ? '1px solid rgba(5,150,105,.15)' : '1px solid #F3F4F6',
                        opacity: hasCards ? 1 : 0.55,
                        cursor: hasCards ? 'pointer' : 'default',
                      }}
                      onClick={() => handleUnitClick(unit)}
                    >
                      <div style={{
                        width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16,
                        background: hasCards ? `linear-gradient(135deg,${meta.ac},${meta.ac}cc)` : '#F3F4F6',
                      }}>
                        {hasCards ? meta.icon : '🔒'}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={S.accUnitName}>{label}</div>
                        <div style={hasCards ? S.accCardCountActive : S.accCardCountEmpty}>
                          {hasCards ? t.cardCount(unit.card_count) : t.noCards}
                        </div>
                      </div>
                      {hasCards && <span style={{ fontSize: 14, color: '#059669' }}>→</span>}
                    </div>
                  );
                };

                const renderAccSemesterUnits = (semesterUnits) => {
                  const hasBab = semesterUnits.some(u => extractBab(u.unit_name) !== null);
                  if (!hasBab) {
                    return semesterUnits.map(u => renderAccUnit(u));
                  }
                  const babGroups = {};
                  semesterUnits.forEach(u => {
                    const bab = extractBab(u.unit_name) || 'other';
                    if (!babGroups[bab]) babGroups[bab] = [];
                    babGroups[bab].push(u);
                  });
                  return Object.keys(babGroups).map(bab => (
                    <div key={bab}>
                      {bab !== 'other' && (
                        <div style={S.babHeader}>{bab}</div>
                      )}
                      {babGroups[bab].map(u => renderAccUnit(
                        u,
                        u.unit_name.replace(/^الباب (الأول|الثاني|الثالث|الرابع):\s*/, ''),
                      ))}
                    </div>
                  ));
                };

                if (semesters.length === 1) {
                  const s0        = semesters[0];
                  const isFirst   = Number(s0) === 1;
                  const semUnits  = bySemester[s0];
                  const completed = semUnits.filter(u => u.card_count > 0).length;
                  const total     = semUnits.length;
                  const pct       = total > 0 ? (completed / total) * 100 : 0;
                  const isOpen    = openSemesters[Number(s0)] !== false;
                  return (
                    <div>
                      <div style={{
                        ...S.accordionCard,
                        border: isFirst ? '2px solid rgba(5,150,105,.25)' : '2px solid rgba(217,119,6,.25)',
                      }}>
                        <div
                          style={{
                            ...S.accordionHeader,
                            background: isFirst
                              ? 'linear-gradient(135deg,#059669,#10B981)'
                              : 'linear-gradient(135deg,#D97706,#F59E0B)',
                          }}
                          onClick={() => setOpenSemesters(prev => ({ ...prev, [Number(s0)]: !prev[Number(s0)] }))}
                        >
                          <div>
                            <div style={S.accordionTitle}>{lang === 'ar' ? (isFirst ? 'الفصل الأول' : 'الفصل الثاني') : (isFirst ? 'Semester 1' : 'Semester 2')}</div>
                            <div style={S.accordionSubtitle}>{lang === 'ar' ? `${completed} من ${total} وحدات مكتملة` : `${completed} of ${total} units completed`}</div>
                          </div>
                          <div style={S.accordionArrow}>{isOpen ? '▲' : '▼'}</div>
                        </div>
                        <div style={{ ...S.progressTrack, background: isFirst ? '#E5E7EB' : '#FEF3C7' }}>
                          <div style={{
                            height: '100%', width: `${pct}%`, borderRadius: 10,
                            background: isFirst
                              ? 'linear-gradient(90deg,#059669,#10B981)'
                              : 'linear-gradient(90deg,#D97706,#F59E0B)',
                            transition: 'width 0.4s ease',
                            position: 'relative', overflow: 'hidden',
                          }} />
                        </div>
                        {isOpen && (
                          <div style={S.accordionBody}>
                            {renderAccSemesterUnits(semUnits)}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, alignItems: 'start' }}>
                    {semesters.map(s => {
                      const isFirst = Number(s) === 1;
                      const semUnits = bySemester[s];
                      const completed = semUnits.filter(u => u.card_count > 0).length;
                      const total     = semUnits.length;
                      const pct       = total > 0 ? (completed / total) * 100 : 0;
                      const isOpen    = !!openSemesters[Number(s)];
                      return (
                        <div key={s} style={{
                          ...S.accordionCard,
                          border: isFirst ? '2px solid rgba(5,150,105,.25)' : '2px solid rgba(217,119,6,.25)',
                        }}>
                          {/* Header */}
                          <div
                            style={{
                              ...S.accordionHeader,
                              background: isFirst
                                ? 'linear-gradient(135deg,#059669,#10B981)'
                                : 'linear-gradient(135deg,#D97706,#F59E0B)',
                            }}
                            onClick={() => setOpenSemesters(prev => ({ ...prev, [Number(s)]: !prev[Number(s)] }))}
                          >
                            <div>
                              <div style={S.accordionTitle}>
                                {lang === 'ar' ? (isFirst ? 'الفصل الأول' : 'الفصل الثاني') : (isFirst ? 'Semester 1' : 'Semester 2')}
                              </div>
                              <div style={S.accordionSubtitle}>
                                {lang === 'ar' ? `${completed} من ${total} وحدات مكتملة` : `${completed} of ${total} units completed`}
                              </div>
                            </div>
                            <div style={S.accordionArrow}>{isOpen ? '▲' : '▼'}</div>
                          </div>
                          {/* Progress bar */}
                          <div style={{
                            ...S.progressTrack,
                            background: isFirst ? '#E5E7EB' : '#FEF3C7',
                          }}>
                            <div style={{
                              height: '100%',
                              width: `${pct}%`,
                              borderRadius: 10,
                              background: isFirst
                                ? 'linear-gradient(90deg,#059669,#10B981)'
                                : 'linear-gradient(90deg,#D97706,#F59E0B)',
                              transition: 'width 0.4s ease',
                              position: 'relative', overflow: 'hidden',
                            }} />
                          </div>
                          {/* Body */}
                          {isOpen && (
                            <div style={S.accordionBody}>
                              {renderAccSemesterUnits(semUnits)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </>
        )}

        {/* ═══ SCREEN 3: Flashcard Review ═══ */}
        {screen === 'cards' && (
          <>
            <div style={S.pageHeader}>
              <button style={S.backBtn} onClick={() => setScreen('units')}>{t.back}</button>
              <h2 style={S.screenTitle}>{selectedUnit?.unit_name}</h2>
              <p style={S.breadcrumb}>{t.progress(currentIndex + 1, cards.length)}</p>
            </div>
            <div style={{ ...S.content, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              {loading && <Spinner />}

              {/* Completion screen */}
              {!loading && completed && (
                <div style={S.completionWrap}>
                  <span style={{ fontSize: 64 }}>🎉</span>
                  <h2 style={S.completionTitle}>{t.completionTitle}</h2>
                  <button style={S.resetBtn} onClick={resetCards}>{t.completionBtn}</button>
                </div>
              )}

              {/* Active card */}
              {!loading && !completed && card && (
                <div style={S.cardHeroWrap}>
                  {/* Card wrapper (parallax host) */}
                  <div
                    style={S.cardWrapper}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    onClick={handleCardClick}
                  >
                    {/* Card face (ref for scaleX + tilt transforms) */}
                    <div
                      ref={cardFaceRef}
                      style={{
                        ...S.cardFace,
                        background: isFlipped
                          ? 'linear-gradient(145deg,#1e1b4b,#312e81,#4f46e5,#818cf8)'
                          : 'linear-gradient(145deg,#064e3b,#065f46,#059669,#34d399)',
                      }}
                    >
                      {!isFlipped ? (
                        /* ── Front ── */
                        <>
                          <div style={S.cardTopRow}>
                            <span style={S.subjectPill}>{selectedSubject?.name}</span>
                            <span style={S.xpBadge}>{t.xpBadge}</span>
                          </div>
                          <span style={{ fontSize: 70, lineHeight: 1 }}>📚</span>
                          <p style={S.cardText}>{card.front_text}</p>
                          <div style={S.cardBottomBar}>{t.flipPrompt}</div>
                        </>
                      ) : (
                        /* ── Back ── */
                        <>
                          <span style={{ fontSize: 70, lineHeight: 1 }}>💡</span>
                          <p style={S.cardText}>{card.back_text}</p>
                          <div style={S.cardBottomBar}>{t.flipBack}</div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Confidence rating */}
                  <div style={S.confidenceWrap}>
                    <p style={S.confidenceLabel}>{t.confidenceLabel}</p>
                    <div style={S.confidenceRow}>
                      {['😕', '😐', '🙂', '😄', '🤩'].map((emoji, i) => (
                        <button
                          key={i}
                          className="fc-conf-btn"
                          style={{
                            ...S.confBtn,
                            opacity:       isFlipped ? 1 : 0.4,
                            pointerEvents: isFlipped ? 'auto' : 'none',
                            transform:     confidence === i + 1 ? 'scale(1.3)' : 'scale(1)',
                          }}
                          onClick={() => handleConfidence(i + 1)}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div style={S.navRow}>
                    <button
                      className="fc-nav-prev"
                      style={{
                        ...S.navBtn,
                        ...S.navBtnPrev,
                        opacity:       currentIndex === 0 ? 0.4 : 1,
                        pointerEvents: currentIndex === 0 ? 'none' : 'auto',
                      }}
                      onClick={() => {
                        setCurrentIndex(i => i - 1);
                        setIsFlipped(false);
                        setConfidence(null);
                      }}
                    >
                      {lang === 'ar' ? '→ السابق' : '← Previous'}
                    </button>
                    <button
                      className="fc-nav-next"
                      style={{
                        ...S.navBtn,
                        ...S.navBtnNext,
                        opacity:       isFlipped ? 1 : 0.4,
                        pointerEvents: isFlipped ? 'auto' : 'none',
                      }}
                      onClick={() => {
                        if (currentIndex === cards.length - 1) {
                          setCompleted(true);
                        } else {
                          setCurrentIndex(i => i + 1);
                          setIsFlipped(false);
                          setConfidence(null);
                        }
                      }}
                    >
                      {lang === 'ar' ? '← التالي' : 'Next →'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        </div>{/* /scrollArea */}
      </div>{/* /zIndex wrapper */}

      {/* Toast */}
      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ── Helper components ─────────────────────────────────────────────────────────

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
      <div className="fc-spinner" />
    </div>
  );
}

function ErrorMsg({ msg, retryLabel, onRetry }) {
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#DC2626' }}>
      <p style={{ marginBottom: 12 }}>{msg}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            background: '#059669', color: '#fff', border: 'none',
            borderRadius: 10, padding: '8px 20px', cursor: 'pointer',
            fontSize: 14, fontFamily: "'Nunito', sans-serif",
          }}
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    height: '100vh',
    overflow: 'hidden',
    background: 'transparent',
    fontFamily: "'Sora', sans-serif",
  },
  stickyHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 20px',
    height: 60,
    minHeight: 60,
    maxHeight: 60,
    background: 'rgba(255,255,255,.85)',
    backdropFilter: 'blur(12px)',
    borderBottom: '1px solid rgba(5,150,105,.12)',
    flexShrink: 0,
    position: 'relative',
    zIndex: 10,
    boxSizing: 'border-box',
  },
  scrollArea: {
    flex: 1,
    overflowY: 'auto',
  },
  headerLogo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  logoText: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 18,
    fontWeight: 700,
  },
  headerBackBtn: {
    background: 'none',
    border: 'none',
    color: '#059669',
    fontFamily: "'Sora', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 0',
  },
  pageHeader: {
    padding: '20px 16px 16px',
    textAlign: 'right',
  },
  pageTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    color: '#1E3A5F',
    margin: 0,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: '#6B7280',
    margin: 0,
    marginBottom: 16,
  },
  screenTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 18,
    fontWeight: 700,
    color: '#1E3A5F',
    margin: 0,
    marginBottom: 4,
  },
  breadcrumb: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: '#6B7280',
    margin: 0,
    marginBottom: 16,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#059669',
    fontFamily: "'Sora', sans-serif",
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '4px 0',
  },
  content: {
    maxWidth: 600,
    margin: '0 auto',
    width: '100%',
    padding: '0 16px',
  },
  subjectsContent: {
    flex: 1,
    maxWidth: 960,
    margin: '0 auto',
    width: '100%',
    padding: '32px 24px 120px',
  },
  subjectGrid: {
    gap: '48px 40px',
    padding: '0 8px',
    maxWidth: 960,
    margin: '0 auto',
    width: '100%',
  },
  arrow: {
    color: '#D1D5DB',
    fontSize: 16,
    marginRight: 'auto',
  },
  unitList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  babHeader: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    color: '#D97706',
    marginTop: 12,
    marginBottom: 6,
    paddingRight: 4,
  },
  semesterHeader: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: '#059669',
    marginTop: 20,
    marginBottom: 8,
    paddingBottom: 6,
    borderBottom: '2px solid rgba(5,150,105,.15)',
  },
  accordionCard: {
    borderRadius: 20,
    overflow: 'hidden',
    marginBottom: 12,
  },
  accordionHeader: {
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  accordionTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: 'white',
  },
  accordionSubtitle: {
    fontSize: 10,
    color: 'rgba(255,255,255,.7)',
    marginTop: 2,
    fontFamily: "'Nunito', sans-serif",
  },
  accordionArrow: {
    background: 'rgba(255,255,255,.2)',
    borderRadius: 8,
    padding: '4px 10px',
    fontSize: 11,
    color: 'white',
    fontWeight: 700,
  },
  progressTrack: {
    height: 6,
    borderRadius: 10,
    overflow: 'hidden',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,.08)',
  },
  accordionBody: {
    background: 'white',
    padding: 10,
  },
  accUnitRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    borderRadius: 16,
    marginBottom: 6,
  },
  accUnitName: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    color: '#1E3A5F',
    lineHeight: 1.3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  accCardCountActive: {
    fontSize: 10,
    color: '#059669',
    fontWeight: 600,
    marginTop: 2,
  },
  accCardCountEmpty: {
    fontSize: 10,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginTop: 2,
  },
  unitCard: {
    background: 'white',
    border: '2px solid #E5E7EB',
    borderRadius: 20,
    padding: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    boxShadow: '0 4px 0 #E5E7EB, 0 8px 20px rgba(5,150,105,.06)',
  },
  seqCircle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    background: 'linear-gradient(135deg,#059669,#10B981)',
    color: '#fff',
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  unitName: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    color: '#1E3A5F',
  },
  cardCountActive: {
    fontSize: 11,
    color: '#059669',
    marginTop: 2,
  },
  cardCountEmpty: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginTop: 2,
  },
  cardHeroWrap: {
    margin: '0 auto',
    maxWidth: 480,
    width: '100%',
    padding: '0 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  cardWrapper: {
    width: '100%',
    maxWidth: 420,
    height: 260,
    borderRadius: 24,
    cursor: 'pointer',
    background: 'transparent',
    position: 'relative',
    perspective: '1000px',
  },
  cardFace: {
    position: 'absolute',
    inset: 0,
    borderRadius: 24,
    boxShadow: '0 8px 32px rgba(0,0,0,.18)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
    overflow: 'hidden',
  },
  cardTopRow: {
    position: 'absolute',
    top: 16,
    left: 16,
    right: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  subjectPill: {
    background: 'rgba(255,255,255,.12)',
    color: '#fff',
    fontSize: 11,
    padding: '3px 10px',
    borderRadius: 20,
  },
  xpBadge: {
    background: 'rgba(255,255,255,.18)',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '3px 10px',
    borderRadius: 20,
  },
  cardText: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    textAlign: 'center',
    marginTop: 12,
    lineHeight: 1.5,
    padding: '0 8px',
  },
  cardBottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'rgba(0,0,0,.18)',
    color: '#fff',
    fontSize: 11,
    padding: '8px',
    borderRadius: '0 0 24px 24px',
    textAlign: 'center',
  },
  confidenceWrap: {
    marginTop: 20,
    textAlign: 'center',
    width: '100%',
  },
  confidenceLabel: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 10,
    marginTop: 0,
  },
  confidenceRow: {
    display: 'flex',
    gap: 10,
    justifyContent: 'center',
  },
  confBtn: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    border: '2px solid transparent',
    background: 'white',
    boxShadow: '0 2px 8px rgba(0,0,0,.08)',
    fontSize: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1,
    padding: 0,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.15s, box-shadow 0.15s',
  },
  completionWrap: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    padding: '40px 24px',
    textAlign: 'center',
  },
  completionTitle: {
    fontFamily: "'Sora', sans-serif",
    fontSize: 22,
    fontWeight: 700,
    color: '#065f46',
    margin: 0,
  },
  resetBtn: {
    background: 'linear-gradient(135deg,#059669,#10B981)',
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '14px 32px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: "'Sora', sans-serif",
    marginTop: 24,
  },
  navRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 12,
    marginTop: 16,
  },
  navBtn: {
    padding: '10px 28px',
    borderRadius: 16,
    border: 'none',
    fontFamily: "'Sora', sans-serif",
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  navBtnPrev: {
    background: 'white',
    color: '#059669',
    border: '2px solid #D1FAE5',
    boxShadow: '0 3px 0 #D1FAE5',
    transition: 'transform .08s, box-shadow .08s',
  },
  navBtnNext: {
    background: 'linear-gradient(135deg,#059669,#10B981)',
    color: 'white',
    border: '2px solid #6EE7B7',
    boxShadow: '0 3px 0 #6EE7B7',
    transition: 'transform .08s, box-shadow .08s',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#1E3A5F',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: 12,
    fontFamily: "'Sora', sans-serif",
    fontSize: 14,
    fontWeight: 600,
    boxShadow: '0 8px 24px rgba(0,0,0,.2)',
    zIndex: 1000,
    whiteSpace: 'nowrap',
  },
};

// ── CSS (hover effects + spinner) ─────────────────────────────────────────────

const CSS = `
.fc-subject-card { transition: transform 0.2s, box-shadow 0.2s; }
.fc-subject-card:hover { transform: translateY(-3px); box-shadow: 0 8px 28px rgba(5,150,105,.16) !important; }
.fc-unit-card { transition: transform 0.2s; }
.fc-unit-card:hover { transform: translateX(-4px); }
.fc-acc-unit-card { transition: transform .15s, box-shadow .15s, border-color .15s; border: 1.5px solid #E5E7EB; }
.fc-acc-unit-card:hover { border-color: rgba(5,150,105,.3) !important; box-shadow: 0 3px 0 #E5E7EB, 0 6px 16px rgba(5,150,105,.1); transform: translateY(-2px); }
.fc-nav-prev:hover { background: #F0FDF4 !important; }
.fc-nav-next:hover { opacity: 0.9; }
.fc-nav-prev:active, .fc-nav-next:active { transform: translateY(3px); }
.fc-nav-prev:active { box-shadow: 0 1px 0 #D1FAE5 !important; }
.fc-nav-next:active { box-shadow: 0 1px 0 #6EE7B7 !important; }
.fc-conf-btn:hover { transform: scale(1.15) !important; box-shadow: 0 4px 12px rgba(0,0,0,.12) !important; }
.fc-spinner { width: 36px; height: 36px; border: 3px solid #E5E7EB; border-top-color: #059669; border-radius: 50%; animation: fc-spin 0.8s linear infinite; }
@keyframes fc-spin { to { transform: rotate(360deg); } }
.fc-subject-grid { display: grid; gap: 16px; }
@media (max-width: 767px) { .fc-subject-grid { grid-template-columns: repeat(2,1fr); } }
@media (min-width: 768px) and (max-width: 1023px) { .fc-subject-grid { grid-template-columns: repeat(3,1fr); } }
@media (min-width: 1024px) { .fc-subject-grid { grid-template-columns: repeat(4,1fr); } }
`;
